import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getMealPlans, getMeals, createMealPlan, updateMealPlan, addToShoppingList, rateMeal, clearMealFromPlan } from '../services/api';
import AddMealModal from './AddMealModal';

const MealPlanner = () => {
  const [mealPlans, setMealPlans] = useState({});
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedMealType, setSelectedMealType] = useState('');
  const [addingToShoppingList, setAddingToShoppingList] = useState(false);
  const [shoppingListMessage, setShoppingListMessage] = useState('');
  const [error, setError] = useState(null);
  
  // Add refs for debouncing and preventing duplicate requests
  const fetchTimeoutRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const isFetchingRef = useRef(false);
  
  // Constants for debouncing
  const DEBOUNCE_DELAY = 2000; // 2 seconds
  const MIN_FETCH_INTERVAL = 10000; // 10 seconds between API refreshes

  const days = [
    { id: 'monday', label: 'Monday' },
    { id: 'tuesday', label: 'Tuesday' },
    { id: 'wednesday', label: 'Wednesday' },
    { id: 'thursday', label: 'Thursday' },
    { id: 'friday', label: 'Friday' },
    { id: 'saturday', label: 'Saturday' },
    { id: 'sunday', label: 'Sunday' },
  ];

  const mealTypes = [
    { id: 'breakfast', label: 'Breakfast' },
    { id: 'lunch', label: 'Lunch' },
    { id: 'dinner', label: 'Dinner' },
  ];

  const fetchData = useCallback(async (force = false) => {
    // Skip if already fetching
    if (isFetchingRef.current) return;
    
    // Check if we should throttle this request
    const now = Date.now();
    if (!force && now - lastFetchTimeRef.current < MIN_FETCH_INTERVAL) {
      return;
    }
    
    try {
      setError(null);
      isFetchingRef.current = true;
      setLoading(true);
      
      const [mealsResponse, plansResponse] = await Promise.all([
        getMeals(),
        getMealPlans(),
      ]);

      lastFetchTimeRef.current = Date.now();
      
      // Make sure meals data is an array
      setMeals(Array.isArray(mealsResponse.data) ? mealsResponse.data : []);

      // Convert array of meal plans to object with day as key
      const plansObject = {};
      
      // Check for both possible data structures:
      // 1. plansResponse.data is the array directly
      // 2. plansResponse.data.data is the array (nested structure)
      const plansArray = Array.isArray(plansResponse.data) 
        ? plansResponse.data 
        : (plansResponse.data && Array.isArray(plansResponse.data.data) 
            ? plansResponse.data.data 
            : []);

      // Process the plans array
      plansArray.forEach(plan => {
        plansObject[plan.day] = plan;
      });
      
      // Create empty plans for days that don't have one yet
      days.forEach(day => {
        if (!plansObject[day.id]) {
          plansObject[day.id] = { day: day.id };
        }
      });

      setMealPlans(plansObject);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Unable to load meal data. Please try again later.');
      setLoading(false);
    } finally {
      isFetchingRef.current = false;
    }
  }, [days]);

  // Debounced version of fetchData
  const debouncedFetchData = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    fetchTimeoutRef.current = setTimeout(() => {
      fetchData();
    }, DEBOUNCE_DELAY);
  }, [fetchData]);

  useEffect(() => {
    fetchData(true); // Force initial load
    
    // Clean up timeout on unmount
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [fetchData]);

  const handleOpenModal = (day, mealType) => {
    setSelectedDay(day);
    setSelectedMealType(mealType);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDay('');
    setSelectedMealType('');
  };

  const handleMealAdded = async (mealId, day, mealType) => {
    try {
      const currentPlan = mealPlans[day];
      const updatedPlan = {
        ...currentPlan,
        [mealType]: mealId
      };

      if (currentPlan.id) {
        // Update existing plan
        await updateMealPlan(currentPlan.id, updatedPlan);
      } else {
        // Create new plan
        await createMealPlan(updatedPlan);
      }

      // Use debounced fetch to avoid multiple rapid requests
      debouncedFetchData();
    } catch (error) {
      console.error('Error updating meal plan:', error);
      setError('Failed to update meal plan. Please try again.');
    }
  };

  const handleAddToShoppingList = async () => {
    setAddingToShoppingList(true);
    setShoppingListMessage('');
    setError(null);
    
    try {
      const addPromises = Object.values(mealPlans)
        .filter(plan => plan.id) // Only plans that exist in the database
        .map(plan => addToShoppingList(plan.id));
      
      await Promise.all(addPromises);
      setShoppingListMessage('Ingredients added to your shopping list!');
      
      setTimeout(() => {
        setShoppingListMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error adding to shopping list:', error);
      setShoppingListMessage('Error adding ingredients to shopping list.');
      setError('Failed to add items to shopping list.');
    } finally {
      setAddingToShoppingList(false);
    }
  };

  const handleRateMeal = async (mealId, rating) => {
    try {
      await rateMeal(mealId, rating);
      debouncedFetchData(); // Use debounced function
    } catch (error) {
      console.error('Error rating meal:', error);
      setError('Failed to rate meal. Please try again.');
    }
  };

  const handleRemoveMeal = async (planId, mealType) => {
    try {
      await clearMealFromPlan(planId, mealType);
      debouncedFetchData(); // Use debounced function
    } catch (error) {
      console.error('Error removing meal:', error);
      setError('Failed to remove meal. Please try again.');
    }
  };

  const renderMealItem = (day, mealType) => {
    const plan = mealPlans[day];
    if (!plan) return null;
    
    const mealId = plan[mealType];
    
    // Ensure meals is an array before calling find
    const meal = Array.isArray(meals) 
      ? meals.find(m => m.id === mealId)
      : null;
    
    if (meal) {
      return (
        <div className="card meal-card">
          {meal.image_url && (
            <img src={meal.image_url} alt={meal.name} className="meal-image"/>
          )}
          <div className="card-body p-2">
            <h6 className="card-title mb-1">{meal.name}</h6>
            <p className="card-text mb-0">
              <small>{meal.description?.substring(0, 60)}...</small>
            </p>
            <div className="meal-actions mt-2">
              <button 
                className={`btn btn-sm me-1 ${meal.rating === 'like' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRateMeal(meal.id, 'like');
                }}
                title="I like this!"
              >
                ❤️
              </button>
              <button 
                className={`btn btn-sm me-1 ${meal.rating === 'dislike' ? 'btn-danger' : 'btn-outline-danger'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRateMeal(meal.id, 'dislike');
                }}
                title="I don't like this"
              >
                🤮
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Remove ${meal.name} from ${mealType}?`)) {
                    handleRemoveMeal(plan.id, mealType);
                  }
                }}
                title="Remove meal"
              >
                ❌
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div 
          className="meal-item-empty"
          onClick={() => handleOpenModal(day, mealType)}
        >
          <i className="bi bi-plus-lg"></i> Add {mealType}
        </div>
      );
    }
  };

  if (loading && !meals.length) {
    return <div>Loading meal planner...</div>;
  }

  return (
    <div className="meal-planner">
      <style>
        {`
          .meal-actions {
            display: flex;
            justify-content: center;
            gap: 5px;
          }
          .meal-actions button {
            font-size: 1rem;
            padding: 2px 8px;
            border-radius: 20px;
          }
          .meal-card {
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .meal-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          }
        `}
      </style>
      <h2>Weekly Meal Planner</h2>
      <p>Click on any day to add a meal. Use emojis to rate meals or remove them from your plan.</p>
      
      <div className="row">
        {days.map(day => (
          <div key={day.id} className="col-md-6 col-lg-4 mb-4">
            <div className="card day-card">
              <div className="card-header">
                <h5 className="mb-0">{day.label}</h5>
              </div>
              <div className="card-body">
                {mealTypes.map(mealType => (
                  <div key={mealType.id} className="mb-3">
                    <div className={`meal-time-badge meal-time-${mealType.id}`}>
                      {mealType.label}
                    </div>
                    <div onClick={() => handleOpenModal(day.id, mealType.id)}>
                      {renderMealItem(day.id, mealType.id)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <button 
        className="btn btn-primary btn-lg add-to-shopping-list-btn"
        onClick={handleAddToShoppingList}
        disabled={addingToShoppingList}
      >
        {addingToShoppingList ? 'Adding...' : 'Add All Ingredients to Shopping List'}
      </button>
      
      {shoppingListMessage && (
        <div className="alert alert-success" style={{ position: 'fixed', bottom: '80px', right: '20px', zIndex: 1000 }}>
          {shoppingListMessage}
        </div>
      )}
      
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      
      <AddMealModal 
        show={showModal}
        handleClose={handleCloseModal}
        day={selectedDay}
        mealType={selectedMealType}
        onMealAdded={handleMealAdded}
      />
    </div>
  );
};

export default MealPlanner; 